/**
 * The live stream, in place of the four-second poll.
 *
 * `GET /api/v1/events` is a `text/event-stream` fed by Postgres `LISTEN/NOTIFY`: `hello` when the
 * socket opens, then `message`, `delivery` and `handoff` frames carrying IDS ONLY, plus a `: ping`
 * comment every 25 seconds so no proxy in between decides the socket is idle. The console
 * re-fetches through the authenticated routes when a frame arrives — which is what keeps ruling 4
 * true: the phone gate runs on `/inbox` and `/leads/:id`, and there is nothing in the stream to
 * gate because there is no customer data in it.
 *
 * Three things this has to get right, and each is a way a live console lies:
 *
 *  - **A dropped stream must be visible.** `EventSource` reconnects on its own and says nothing;
 *    a console whose socket died at 09:00 looks identical to one where nothing has happened since.
 *    So the state is published (`live` / `reconnecting` / `off`) and the Shell renders it.
 *  - **A reconnect must refetch, not resume.** The stream has no replay: whatever happened while
 *    the socket was down was never queued anywhere. So `hello` — which arrives on every
 *    connection, including every reconnect — invalidates the lists rather than being ignored as a
 *    greeting.
 *  - **Invalidate, never patch.** A frame says "conversation X changed", not what it changed to.
 *    Writing the change into the cache from an id would be inventing it.
 *
 * `EventSource` is a browser built-in: no dependency, and it does the reconnect/backoff itself.
 * What it cannot do is send a header, which is why the route authenticates with the session
 * COOKIE and not with `x-csrf` — a read needs no CSRF token.
 *
 * Author: Danish Khan
 */
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { API_SOURCE } from './api.ts';
import { useSession } from './session.tsx';

export type StreamState = 'connecting' | 'live' | 'reconnecting' | 'off';

export interface StreamStatus {
  state: StreamState;
  /** When the last frame of any kind arrived, including the ping. Null until the first one. */
  lastEventAt: number | null;
  /** How many times the socket has come back after dropping. Visible, so a flapping link is too. */
  reconnects: number;
}

/** What the server sends. Ids only — see `packages/core/src/api/sse.ts`. */
interface H1Frame {
  type?: string;
  tenantId?: string;
  id?: string | null;
  conversationId?: string | null;
  direction?: 'in' | 'out';
  status?: string;
  at?: string;
}

const Ctx = createContext<StreamStatus>({ state: 'off', lastEventAt: null, reconnects: 0 });

/**
 * If a ping has not landed in this long the socket is dead even if `EventSource` has not noticed.
 * The server pings every 25 s; 70 s is nearly three missed beats, which is a drop and not jitter.
 */
const SILENCE_MS = 70_000;

/** Backoff between our own reconnect attempts, in ms. Capped, so a long outage still recovers. */
export const RETRY_MS = [1_000, 2_000, 4_000, 8_000, 15_000] as const;
const retryDelay = (attempt: number): number => RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)] ?? 15_000;

/**
 * How long a socket may stay open without the API greeting it before it is treated as dead.
 *
 * `hello` is the first thing `/api/v1/events` writes, so it arrives in milliseconds on a healthy
 * connection. Anything approaching this means something accepted the TCP connection and there is
 * no h1-core behind it. Measured in Chrome with the API killed: the Vite proxy went on accepting
 * connections and `open` kept firing, so `open` alone is not evidence of anything.
 */
const HELLO_TIMEOUT_MS = 5_000;

export function EventStreamProvider({ children }: { children: ReactNode }): ReactNode {
  const { session } = useSession();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StreamStatus>({
    state: API_SOURCE === 'mock' ? 'off' : 'connecting',
    lastEventAt: null,
    reconnects: 0,
  });
  // Read inside the timers without making them dependencies, so the effect installs once.
  const lastRef = useRef<number | null>(null);
  const openedRef = useRef(0);

  useEffect(() => {
    // The mock build has no server to stream from, and an unauthenticated stream is a 401 loop.
    if (API_SOURCE === 'mock' || !session) {
      setStatus({ state: 'off', lastEventAt: null, reconnects: 0 });
      return;
    }

    let source: EventSource | null = null;
    let retryTimer: number | undefined;
    let helloTimer: number | undefined;
    let attempt = 0;
    let disposed = false;

    const touch = (): void => {
      lastRef.current = Date.now();
      setStatus((s) => (s.state === 'live' ? { ...s, lastEventAt: lastRef.current } : s));
    };

    /**
     * Reconnect ourselves rather than trusting `EventSource` to do it.
     *
     * The browser's own retry is not dependable here and the failure it produces is silent. Watched
     * in Chrome against this very server: killing the API left the page saying "Reconnecting…"
     * forever — `readyState` stayed CONNECTING and no further attempt ever landed, while a
     * `new EventSource` created by hand in the same tab connected instantly. The reason is the
     * shape of the failure: with nothing listening, the dev proxy (and Caddy in Phase 3) answers a
     * plain HTTP error, and a response that is not `text/event-stream` is a FATAL failure for
     * `EventSource` — the spec's retry only covers a stream that was dropped mid-flight.
     *
     * So: every error tears the socket down and schedules a fresh one. It is a few more lines and
     * it is the difference between a console that comes back and a console that looks fine while
     * telling you nothing, which is the failure this whole badge exists to prevent.
     */
    const scheduleReconnect = (): void => {
      if (disposed || retryTimer !== undefined) return;
      if (helloTimer !== undefined) {
        window.clearTimeout(helloTimer);
        helloTimer = undefined;
      }
      const delay = retryDelay(attempt);
      attempt += 1;
      setStatus((s) => ({ ...s, state: 'reconnecting' }));
      retryTimer = window.setTimeout(() => {
        retryTimer = undefined;
        connect();
      }, delay);
    };

    function connect(): void {
      if (disposed) return;
      source?.close();
      const es = new EventSource('/api/v1/events', { withCredentials: true });
      source = es;

      /**
       * `open` is NOT "live". It only means something accepted a socket.
       *
       * Watched in Chrome with h1-core killed: the dev proxy kept accepting connections and
       * firing `open` with nothing behind it, and the badge read "Live" for seventy-one seconds
       * while the console was being told nothing at all. In Phase 3 a proxy in front does the
       * same. So `open` starts a clock, and only `hello` — which the route writes as its first
       * frame — stops it.
       */
      es.addEventListener('open', () => {
        setStatus((s) => (s.state === 'live' ? s : { ...s, state: 'connecting' }));
        if (helloTimer !== undefined) window.clearTimeout(helloTimer);
        helloTimer = window.setTimeout(() => {
          helloTimer = undefined;
          es.close();
          scheduleReconnect();
        }, HELLO_TIMEOUT_MS);
      });

      /**
       * `hello` arrives on EVERY connection, so this is both "we are live" and the reconnect
       * handler. The stream has no replay — refetching every open list is the only way to learn
       * what was missed while the socket was down.
       */
      es.addEventListener('hello', () => {
        if (helloTimer !== undefined) {
          window.clearTimeout(helloTimer);
          helloTimer = undefined;
        }
        attempt = 0;
        openedRef.current += 1;
        lastRef.current = Date.now();
        setStatus((s) => ({
          state: 'live',
          lastEventAt: lastRef.current,
          // The first greeting is not a reconnect; every one after it is.
          reconnects: openedRef.current > 1 ? s.reconnects + 1 : s.reconnects,
        }));
        // Not on the FIRST hello: the pages have just fetched, and refetching them a beat later is
        // two requests for one page load. Every later hello is a reconnect and must refetch.
        if (openedRef.current > 1) void queryClient.invalidateQueries();
      });

      const onChange = (raw: MessageEvent<string>): void => {
        touch();
        let frame: H1Frame = {};
        try {
          frame = JSON.parse(raw.data) as H1Frame;
        } catch {
          // A frame we cannot read is still evidence something changed; refetch broadly.
        }
        // Invalidate by id where the frame names one, and the list queries always: an inbox row's
        // preview, its unanswered clock and its tier all move when a message lands.
        if (frame.conversationId) {
          void queryClient.invalidateQueries({ queryKey: ['messages', frame.conversationId] });
        }
        void queryClient.invalidateQueries({ queryKey: ['inbox'] });
        void queryClient.invalidateQueries({ queryKey: ['home'] });
        void queryClient.invalidateQueries({ queryKey: ['lead'] });
        void queryClient.invalidateQueries({ queryKey: ['metrics'] });
        if (frame.type === 'handoff') {
          void queryClient.invalidateQueries({ queryKey: ['handoffs'] });
          void queryClient.invalidateQueries({ queryKey: ['pipeline'] });
        }
        if (frame.type === 'delivery') void queryClient.invalidateQueries({ queryKey: ['health'] });
      };

      es.addEventListener('message', onChange as EventListener);
      es.addEventListener('delivery', onChange as EventListener);
      es.addEventListener('handoff', onChange as EventListener);

      es.addEventListener('error', () => {
        es.close();
        scheduleReconnect();
      });
    }

    connect();

    /**
     * The silence guard. A TCP connection can be black-holed — a sleeping laptop, a middlebox —
     * without either end getting a FIN, and `EventSource` will sit on a dead socket forever with
     * `readyState` OPEN and no error. The server pings every 25 s, so nothing for 70 s is three
     * missed beats: tear it down and dial again rather than merely relabelling the badge.
     */
    const watchdog = window.setInterval(() => {
      const last = lastRef.current;
      if (last !== null && Date.now() - last > SILENCE_MS && source?.readyState === EventSource.OPEN) {
        source.close();
        scheduleReconnect();
      }
    }, 10_000);

    return () => {
      disposed = true;
      window.clearInterval(watchdog);
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
      if (helloTimer !== undefined) window.clearTimeout(helloTimer);
      source?.close();
    };
  }, [session, queryClient]);

  return <Ctx.Provider value={status}>{children}</Ctx.Provider>;
}

export function useStreamStatus(): StreamStatus {
  return useContext(Ctx);
}
